import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { useTexts } from '../context/TextsContext';
import LanguageSwitcher from '../components/LanguageSwitcher';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Generate stable particle styles outside component
const BOKEH_LIGHTS = [...Array(50)].map(() => ({
  left: `${Math.random() * 100}%`,
  top: `${Math.random() * 100}%`,
  size: `${Math.random() * 60 + 20}px`,
  animationDelay: `${Math.random() * 3}s`,
  animationDuration: `${3 + Math.random() * 4}s`
}));

// Text component that shows smooth transition when text loads
const SmoothText = ({ textKey, fallback, className, as: Tag = 'span' }) => {
  const { t, texts } = useTexts();
  const text = t(textKey, fallback);
  const hasLoaded = Object.keys(texts).length > 0;
  
  return (
    <Tag 
      className={`${className || ''} ${hasLoaded ? 'text-loaded' : 'text-loading'}`}
    >
      {text}
    </Tag>
  );
};

const HomePage = () => {
  const [candles, setCandles] = useState([]);
  const [fieldData, setFieldData] = useState(null);
  const [zoom, setZoom] = useState(0.5);
  const [totalCandles, setTotalCandles] = useState(0);
  const [showModal, setShowModal] = useState(false);
  const [selectedCandle, setSelectedCandle] = useState(null);
  const [meditationCandle, setMeditationCandle] = useState(null); // For contemplation mode
  const [loading, setLoading] = useState(false);
  const [heroLoaded, setHeroLoaded] = useState(false);
  const [isGift, setIsGift] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    message: '',
    amount: 200,
    buyer_email: '',
    recipient_name: '',
    recipient_email: '',
    gift_message: ''
  });
  const [giftSent, setGiftSent] = useState(false);
  const fieldRef = useRef(null);
  const [paymentProviders, setPaymentProviders] = useState([]);
  
  const navigate = useNavigate();
  const { isAuthenticated, user } = useAuth();
  const { t } = useTexts();

  // Open meditation/contemplation mode
  const openMeditation = async (light) => {
    // If it's a cluster without name, try to fetch details
    if (light.represents > 1 && !light.name) return;
    
    // If we already have the name, use it
    if (light.name) {
      setMeditationCandle({
        id: light.id,
        name: light.name,
        message: light.message,
        amount: light.amount || 200,
        size: light.size
      });
    } else if (light.id && !light.id.startsWith('cluster_')) {
      // Fetch candle details from API
      try {
        const response = await axios.get(`${API}/candles/${light.id}`);
        if (response.data) {
          setMeditationCandle({
            id: response.data.id,
            name: response.data.name,
            message: response.data.message,
            amount: response.data.amount || 200,
            size: light.size
          });
        }
      } catch (error) {
        console.error('Error fetching candle:', error);
      }
    }
  };

  const closeMeditation = () => {
    setMeditationCandle(null);
  };

  // Load payment providers from settings
  const loadPaymentProviders = async () => {
    try {
      const response = await axios.get(`${API}/admin/public/settings/payment`);
      setPaymentProviders(response.data.providers || []);
      // Set default to first enabled provider
      if (response.data.providers?.length > 0) {
        setPaymentProvider(response.data.providers[0].id);
      }
    } catch (error) {
      // Fallback to defaults
      setPaymentProviders([
        { id: 'stripe', name: 'Stripe', enabled: true, hint: 'Kreditkarte' },
        { id: 'whop', name: 'Whop', enabled: true, hint: 'Karte, Apple Pay, Google Pay' }
      ]);
    }
  };

  // Load field data with clustering
  const loadFieldData = async (zoomLevel) => {
    try {
      const response = await axios.get(`${API}/candles/field?zoom=${zoomLevel}`);
      setFieldData(response.data);
      setTotalCandles(response.data.total_candles);
      
      // Adjust zoom if outside allowed range
      if (zoomLevel < response.data.zoom_min) {
        setZoom(response.data.zoom_min);
      }
    } catch (error) {
      console.error('Fehler beim Laden des Lichtfelds:', error);
      // Fallback to old method
      loadCandles();
    }
  };

  const loadCandles = async () => {
    try {
      const response = await axios.get(`${API}/candles`);
      setCandles(response.data);
      setTotalCandles(response.data.length);
    } catch (error) {
      console.error('Fehler beim Laden der Kerzen:', error);
    }
  };

  useEffect(() => {
    loadFieldData(zoom);
    loadPaymentProviders();
    // Hero animation trigger
    setTimeout(() => setHeroLoaded(true), 100);
  }, []);

  // Reload field when zoom changes (debounced)
  useEffect(() => {
    const timer = setTimeout(() => {
      loadFieldData(zoom);
    }, 150);
    return () => clearTimeout(timer);
  }, [zoom]);

  // Handle scroll wheel for zoom
  const handleWheel = useCallback((e) => {
    if (!fieldRef.current) return;
    
    const rect = fieldRef.current.getBoundingClientRect();
    const isOverField = e.clientY >= rect.top && e.clientY <= rect.bottom;
    
    if (isOverField) {
      e.preventDefault();
      const zoomDelta = e.deltaY > 0 ? -0.05 : 0.05;
      setZoom(prev => {
        const newZoom = Math.max(fieldData?.zoom_min || 0.1, Math.min(1.0, prev + zoomDelta));
        return newZoom;
      });
    }
  }, [fieldData?.zoom_min]);

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('success') === 'true') {
      setTimeout(() => {
        loadFieldData(zoom);
        window.history.replaceState({}, '', window.location.pathname);
      }, 2000);
    }
    if (urlParams.get('gift_sent') === 'true') {
      setGiftSent(true);
      setTimeout(() => {
        setGiftSent(false);
        window.history.replaceState({}, '', window.location.pathname);
      }, 5000);
    }
  }, []);

  // Add wheel event listener
  useEffect(() => {
    const field = fieldRef.current;
    if (field) {
      field.addEventListener('wheel', handleWheel, { passive: false });
      return () => field.removeEventListener('wheel', handleWheel);
    }
  }, [handleWheel]);

  const [paymentProvider, setPaymentProvider] = useState('stripe'); // 'stripe' or 'whop'

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const payload = {
        name: formData.name,
        message: formData.message,
        amount: formData.amount,
        is_gift: isGift,
        buyer_email: formData.buyer_email || null
      };
      
      if (isGift) {
        payload.recipient_name = formData.recipient_name;
        payload.recipient_email = formData.recipient_email;
        payload.gift_message = formData.gift_message;
      }
      
      // Choose checkout endpoint based on payment provider
      const checkoutEndpoint = paymentProvider === 'whop' ? `${API}/checkout/whop` : `${API}/checkout`;
      
      const response = await axios.post(checkoutEndpoint, payload, {
        withCredentials: true
      });
      window.location.href = response.data.checkout_url;
    } catch (error) {
      console.error('Fehler beim Erstellen der Checkout-Session:', error);
      const errorMsg = error.response?.data?.detail || 'Ein Fehler ist aufgetreten. Bitte versuche es erneut.';
      alert(errorMsg);
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      message: '',
      amount: 200,
      buyer_email: '',
      recipient_name: '',
      recipient_email: '',
      gift_message: ''
    });
    setIsGift(false);
  };

  const openModal = () => {
    resetForm();
    setShowModal(true);
  };

  const getAmountLabel = (cents) => {
    const euros = cents / 100;
    return `${euros}€`;
  };

  const getCandleSize = (amount) => {
    if (amount === 200) return 'small';
    if (amount === 500) return 'medium';
    return 'large';
  };

  const getCandleImage = (amount) => {
    if (amount === 200) return 'https://images.unsplash.com/photo-1613068431228-8cb6a1e92573?w=400&q=85';
    if (amount === 500) return 'https://images.unsplash.com/photo-1601479604588-68d9e6d386b5?w=400&q=85';
    return 'https://images.unsplash.com/photo-1530043689697-bddc19df3c74?w=400&q=85';
  };

  const handleCandleClick = (candle) => {
    setSelectedCandle(candle);
  };

  const closeCandleDetail = () => {
    setSelectedCandle(null);
  };

  return (
    <div className="app">
      {/* Navigation Bar */}
      <nav className="main-nav">
        <div className="nav-content">
          <span className="nav-logo">{t('nav.logo', 'Place of Lights')}</span>
          <div className="nav-actions">
            <LanguageSwitcher />
            {isAuthenticated ? (
              <>
                <button 
                  className="nav-link"
                  onClick={() => navigate('/dashboard')}
                  data-testid="nav-dashboard-link"
                >
                  {t('nav.my_candles', 'Meine Kerzen')}
                </button>
                <span className="nav-user-name">{user?.name}</span>
              </>
            ) : (
              <button 
                className="nav-login-button"
                onClick={() => navigate('/login')}
                data-testid="nav-login-button"
              >
                {t('nav.login', 'Anmelden')}
              </button>
            )}
          </div>
        </div>
      </nav>

      {/* Cinematic Hero Section */}
      <section className={`hero-cinematic ${heroLoaded ? 'loaded' : ''}`}>
        <div className="void-black"></div>
        
        {/* Bokeh lights in background */}
        <div className="bokeh-field">
          {BOKEH_LIGHTS.map((style, i) => (
            <div 
              key={i} 
              className="bokeh-light" 
              style={{
                left: style.left,
                top: style.top,
                width: style.size,
                height: style.size,
                animationDelay: style.animationDelay,
                animationDuration: style.animationDuration
              }}
            ></div>
          ))}
        </div>

        {/* Single column layout */}
        <div className="hero-single-column">
          {/* 1. Title */}
          <h1 className="hero-title-cinematic">
            <span className="title-line">{t('hero.title_line1', 'Place of')}</span>
            <span className="title-line">{t('hero.title_line2', 'Lights')}</span>
          </h1>

          {/* 2. Video Candle */}
          <div className="hero-candle-video-container">
            <video 
              className="hero-candle-video"
              autoPlay 
              loop 
              muted 
              playsInline
              poster=""
            >
              <source src="https://customer-assets.emergentagent.com/job_33a9670e-b794-46b7-93ac-be6fd3a8783f/artifacts/ax92344p_candle_hero_final_loop.mp4" type="video/mp4" />
            </video>
            <div className="hero-candle-video-glow"></div>
          </div>

          {/* OLD CSS Candle - kept for reference
          <div className="central-flame-container realistic">
            <div className="flame-realistic">
              <div className="flame-outer"></div>
              <div className="flame-middle"></div>
              <div className="flame-inner"></div>
              <div className="flame-core-white"></div>
            </div>
            <div className="flame-glow-realistic"></div>
            <div className="wick"></div>
            <div className="candle-wax">
              <div className="wax-drip"></div>
              <div className="wax-drip delay-1"></div>
              <div className="wax-drip delay-2"></div>
            </div>
          </div>
          */}

          {/* 3. Taglines */}
          <p className="hero-tagline-cinematic">
            {t('hero.tagline_line1', 'Ein Moment der Stille')}<br />
            {t('hero.tagline_line2', 'Ein Licht in der Dunkelheit')}<br />
            {t('hero.tagline_line3', 'Eine Verbindung zur Ewigkeit')}
          </p>

          {/* 4. CTA */}
          <button 
            className="hero-cta-cinematic"
            onClick={openModal}
            data-testid="light-candle-button"
          >
            <span className="cta-glow"></span>
            <span className="cta-text">{t('hero.cta_button', 'Dein Licht hinzufügen')}</span>
          </button>
          
          <p className="hero-charity-cinematic">
            <span className="charity-symbol">✦</span>
            {t('hero.charity_note', 'Ein Teil fließt in wohltätige Projekte')}
          </p>

          {/* Free Candle CTA for non-logged-in users */}
          {!isAuthenticated && (
            <div className="free-candle-cta" data-testid="free-candle-cta">
              <div className="free-candle-cta-glow"></div>
              <div className="free-candle-cta-content">
                <div className="free-candle-icon">🎁</div>
                <div className="free-candle-cta-text">
                  <SmoothText 
                    textKey="hero.free_candle_headline" 
                    fallback="Kostenlos Licht verschenken!" 
                    className="free-candle-headline"
                  />
                  <SmoothText 
                    textKey="hero.free_candle_subtext" 
                    fallback="Registriere dich und verschenke kostenlose Teelichter an Freunde & Familie" 
                    className="free-candle-subtext"
                  />
                </div>
                <button 
                  className="free-candle-cta-button"
                  onClick={() => navigate('/login')}
                  data-testid="free-candle-register-btn"
                >
                  {t('hero.free_candle_button', 'Jetzt registrieren')}
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Scroll indicator */}
        <div className="scroll-indicator-cinematic">
          <div className="scroll-line"></div>
          <div className="scroll-text">{t('hero.scroll_hint', 'Entdecke das Lichtfeld')}</div>
        </div>
      </section>

      {/* Field of Lights Section */}
      <section className="field-section" ref={fieldRef}>
        <div className="field-header">
          <h2 className="field-title">{t('field.title', 'Das Lichtfeld der Menschlichkeit')}</h2>
          <p className="field-subtitle">
            {totalCandles} {t('field.candles_count', 'leuchtende Kerzen')} • {t('field.subtitle', 'Jede ein Zeichen der Hoffnung')}
          </p>
        </div>

        {totalCandles === 0 ? (
          <div className="no-candles-container">
            <div className="no-candles-glow"></div>
            <p className="no-candles-text">{t('field.empty_title', 'Das Lichtfeld wartet auf das erste Licht')}</p>
            <p className="no-candles-subtext">{t('field.empty_subtitle', 'Sei der Erste und bringe Licht in die Dunkelheit')}</p>
            <button 
              className="no-candles-button"
              onClick={() => setShowModal(true)}
            >
              {t('field.empty_button', 'Erste Kerze anzünden')}
            </button>
          </div>
        ) : (
          <div className="field-of-lights" style={{ '--zoom-level': zoom }}>
            {/* Zoom indicator */}
            <div className="zoom-indicator">
              <span className="zoom-icon">🔍</span>
              <input
                type="range"
                min={fieldData?.zoom_min || 0.1}
                max="1"
                step="0.05"
                value={zoom}
                onChange={(e) => setZoom(parseFloat(e.target.value))}
                className="zoom-slider"
                data-testid="zoom-slider"
              />
              <span className="zoom-hint">{t('field.zoom_hint', 'Scrollen zum Zoomen')}</span>
            </div>
            
            <div className="field-perspective">
              {(fieldData?.lights || []).map((light, index) => {
                // Calculate visual properties based on zoom and light data
                const isCluster = light.represents > 1;
                const sizeMultiplier = isCluster ? Math.min(2, 1 + light.represents / 50) : 1;
                const canShowDetails = zoom >= 0.7 && !isCluster;
                const isClickable = !light.id.startsWith('cluster_');
                
                // Flicker delay for natural animation
                const flickerDelay = (index % 30) / 10;
                
                return (
                  <div 
                    key={light.id} 
                    className={`candle-light ${light.size} ${isCluster ? 'cluster' : ''} ${isClickable ? 'clickable' : ''}`}
                    data-testid="candle-light"
                    style={{
                      animationDelay: `${index * 0.02}s`,
                      left: `${light.x}%`,
                      top: `${light.y}%`,
                      '--flicker-delay': `${flickerDelay}s`,
                      '--glow-delay': `${flickerDelay + 0.5}s`,
                      '--intensity': light.intensity,
                      '--size-multiplier': sizeMultiplier,
                      opacity: 0,
                      animation: `fadeInLight 0.5s ease-out ${index * 0.02}s forwards`
                    }}
                    onClick={() => isClickable && openMeditation(light)}
                    title={light.name || ''}
                  >
                    <div className="candle-flame" style={{ animationDelay: `${flickerDelay}s` }}></div>
                    <div className="candle-body"></div>
                    <div className="candle-glow-ring"></div>
                    {/* Show name on hover at high zoom */}
                    {canShowDetails && light.name && (
                      <div className="candle-hover-name">{light.name}</div>
                    )}
                  </div>
                );
              })}
            </div>
            <div className="field-ambient-glow" style={{ opacity: 0.3 + zoom * 0.4 }}></div>
          </div>
        )}

        <div className="field-stats">
          <div className="stat-item">
            <div className="stat-number">{totalCandles}</div>
            <div className="stat-label">{t('stats.total', 'Lichter gesamt')}</div>
          </div>
        </div>
      </section>

      {/* Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <button 
              className="modal-close"
              onClick={() => setShowModal(false)}
              data-testid="close-modal-button"
            >
              ×
            </button>
            <div className="modal-header">
              <h2 className="modal-title">{isGift ? t('modal.gift_title', '🎁 Kerze verschenken') : t('modal.title', 'Dein Licht hinzufügen')}</h2>
              <p className="modal-subtitle">{isGift ? t('modal.gift_subtitle', 'Schenke jemandem ein leuchtendes Zeichen') : t('modal.subtitle', 'Werde Teil des leuchtenden Feldes')}</p>
            </div>
            
            {/* Tab Toggle */}
            <div className="modal-tabs">
              <button 
                type="button"
                className={`modal-tab ${!isGift ? 'active' : ''}`}
                onClick={() => setIsGift(false)}
                data-testid="tab-self"
              >
                {t('modal.tab_self', 'Für mich')}
              </button>
              <button 
                type="button"
                className={`modal-tab ${isGift ? 'active' : ''}`}
                onClick={() => setIsGift(true)}
                data-testid="tab-gift"
              >
                {t('modal.tab_gift', '🎁 Verschenken')}
              </button>
            </div>
            
            <form onSubmit={handleSubmit} className="candle-form">
              {/* Email field for non-logged-in users */}
              {!isAuthenticated && !isGift && (
                <div className="form-group email-linking-group">
                  <label htmlFor="buyer_email">{t('modal.form.email_label', 'Deine E-Mail (optional)')}</label>
                  <input
                    id="buyer_email"
                    type="email"
                    placeholder={t('modal.form.email_placeholder', 'Für spätere Kontoverknüpfung')}
                    value={formData.buyer_email}
                    onChange={(e) => setFormData({ ...formData, buyer_email: e.target.value })}
                    data-testid="buyer-email-input"
                  />
                  <p className="email-hint">{t('modal.form.email_hint', 'Wenn du später ein Konto mit dieser E-Mail erstellst, wird die Kerze automatisch verknüpft.')}</p>
                </div>
              )}

              <div className="form-group">
                <label htmlFor="name">{t('modal.form.name_label', 'Für wen leuchtet das Licht?')}</label>
                <input
                  id="name"
                  type="text"
                  placeholder={t('modal.form.name_placeholder', 'z.B. Für den Frieden, Für Mama, Für die Hoffnung')}
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  maxLength={50}
                  data-testid="candle-name-input"
                />
              </div>

              <div className="form-group">
                <label htmlFor="message">{t('modal.form.message_label', 'Botschaft der Kerze (optional)')}</label>
                <textarea
                  id="message"
                  placeholder={t('modal.form.message_placeholder', 'Eine persönliche Nachricht...')}
                  value={formData.message}
                  onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                  maxLength={200}
                  rows={3}
                  data-testid="candle-message-input"
                />
              </div>

              {/* Gift-specific fields */}
              {isGift && (
                <div className="gift-fields">
                  <div className="form-group">
                    <label htmlFor="recipient_name">{t('modal.gift.recipient_name_label', 'Name des Empfängers')}</label>
                    <input
                      id="recipient_name"
                      type="text"
                      placeholder={t('modal.gift.recipient_name_placeholder', 'z.B. Anna')}
                      value={formData.recipient_name}
                      onChange={(e) => setFormData({ ...formData, recipient_name: e.target.value })}
                      maxLength={50}
                      data-testid="gift-recipient-name"
                    />
                  </div>
                  
                  <div className="form-group">
                    <label htmlFor="recipient_email">{t('modal.gift.recipient_email_label', 'E-Mail des Empfängers *')}</label>
                    <input
                      id="recipient_email"
                      type="email"
                      placeholder={t('modal.gift.recipient_email_placeholder', 'anna@example.com')}
                      value={formData.recipient_email}
                      onChange={(e) => setFormData({ ...formData, recipient_email: e.target.value })}
                      required={isGift}
                      data-testid="gift-recipient-email"
                    />
                  </div>
                  
                  <div className="form-group">
                    <label htmlFor="gift_message">{t('modal.gift.message_label', 'Persönliche Geschenknachricht (optional)')}</label>
                    <textarea
                      id="gift_message"
                      placeholder={t('modal.gift.message_placeholder', 'z.B. In Gedanken bei dir...')}
                      value={formData.gift_message}
                      onChange={(e) => setFormData({ ...formData, gift_message: e.target.value })}
                      maxLength={300}
                      rows={3}
                      data-testid="gift-message"
                    />
                  </div>
                  
                  <div className="gift-info-box">
                    <p>{t('modal.gift.info_text', '📧 Der Empfänger erhält eine E-Mail mit einem Link zum Geschenk')}</p>
                  </div>
                </div>
              )}

              <div className="form-group">
                <label>{t('modal.form.size_label', 'Größe des Lichts')}</label>
                <div className="price-options">
                  <button
                    type="button"
                    className={`price-option ${formData.amount === 200 ? 'selected' : ''}`}
                    onClick={() => setFormData({ ...formData, amount: 200 })}
                    data-testid="price-option-2"
                  >
                    <div className="option-light small-light"></div>
                    <span className="price-label">{t('modal.size.small', 'Klein')}</span>
                    <span className="price-amount">2€</span>
                  </button>
                  <button
                    type="button"
                    className={`price-option ${formData.amount === 500 ? 'selected' : ''}`}
                    onClick={() => setFormData({ ...formData, amount: 500 })}
                    data-testid="price-option-5"
                  >
                    <div className="option-light medium-light"></div>
                    <span className="price-label">{t('modal.size.medium', 'Mittel')}</span>
                    <span className="price-amount">5€</span>
                  </button>
                  <button
                    type="button"
                    className={`price-option ${formData.amount === 1000 ? 'selected' : ''}`}
                    onClick={() => setFormData({ ...formData, amount: 1000 })}
                    data-testid="price-option-10"
                  >
                    <div className="option-light large-light"></div>
                    <span className="price-label">{t('modal.size.large', 'Groß')}</span>
                    <span className="price-amount">10€</span>
                  </button>
                </div>
              </div>

              {/* Payment Provider Selection - Dynamic from Settings */}
              {paymentProviders.length > 0 && (
                <div className="form-group payment-provider-group">
                  <label>{t('modal.payment.label', 'Zahlungsmethode')}</label>
                  <div className="payment-options">
                    {paymentProviders.map((provider) => (
                      <button
                        key={provider.id}
                        type="button"
                        className={`payment-option ${paymentProvider === provider.id ? 'selected' : ''}`}
                        onClick={() => setPaymentProvider(provider.id)}
                        data-testid={`payment-${provider.id}`}
                      >
                        <span className="payment-icon">
                          {provider.id === 'stripe' ? '💳' : provider.id === 'whop' ? '🛒' : '💰'}
                        </span>
                        <span className="payment-label">{provider.name}</span>
                        <span className="payment-hint">
                          {t(`modal.payment.${provider.id}_hint`, provider.hint)}
                        </span>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              <button 
                type="submit" 
                className="submit-button"
                disabled={loading}
                data-testid="checkout-button"
              >
                {loading ? (
                  <span className="loading-spinner"></span>
                ) : (
                  <>
                    <span className="button-glow"></span>
                    <span className="button-text">
                      {isGift ? `${t('modal.button.gift', 'Geschenk kaufen')} (${getAmountLabel(formData.amount)})` : `${t('modal.button.light', 'Licht anzünden')} (${getAmountLabel(formData.amount)})`}
                    </span>
                  </>
                )}
              </button>
            </form>
          </div>
        </div>
      )}

      {/* Candle Detail Modal */}
      {selectedCandle && (
        <div className="modal-overlay" onClick={closeCandleDetail}>
          <div className="candle-detail-modal" onClick={(e) => e.stopPropagation()}>
            <button 
              className="modal-close"
              onClick={closeCandleDetail}
            >
              ×
            </button>
            <div className="candle-detail-content">
              <div className="candle-detail-image">
                <img 
                  src={getCandleImage(selectedCandle.amount)} 
                  alt={selectedCandle.name}
                />
                <div className="candle-detail-flame"></div>
              </div>
              <div className="candle-detail-info">
                <h3 className="candle-detail-name">{selectedCandle.name}</h3>
                {selectedCandle.message && (
                  <p className="candle-detail-message">"{selectedCandle.message}"</p>
                )}
                <div className="candle-detail-meta">
                  <span className="detail-size">
                    {selectedCandle.amount === 200 && t('candle.size.small', 'Kleines Licht')}
                    {selectedCandle.amount === 500 && t('candle.size.medium', 'Mittleres Licht')}
                    {selectedCandle.amount === 1000 && t('candle.size.large', 'Großes Licht')}
                  </span>
                  <span className="detail-amount">{getAmountLabel(selectedCandle.amount)}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Success Message */}
      {new URLSearchParams(window.location.search).get('success') === 'true' && (
        <div className="success-banner" data-testid="success-message">
          {t('messages.success', '✨ Dein Licht leuchtet jetzt im Feld! Danke für deine Unterstützung.')}
        </div>
      )}

      {/* Gift Sent Message */}
      {giftSent && (
        <div className="success-banner gift-sent-banner" data-testid="gift-sent-message">
          {t('messages.gift_sent', '🎁 Geschenk gesendet! Der Empfänger erhält eine E-Mail mit dem Link.')}
        </div>
      )}

      {/* Footer */}
      <footer className="footer">
        <div className="footer-content">
          <div className="footer-cta-section">
            <h3 className="footer-cta-title">{t('footer.cta_title', 'Dein Licht hinzufügen')}</h3>
            <p className="footer-cta-text">{t('footer.cta_subtitle', 'Werde Teil dieser leuchtenden Gemeinschaft')}</p>
            <button 
              className="footer-cta-button"
              onClick={openModal}
              data-testid="footer-light-candle-button"
            >
              <span className="button-glow"></span>
              <span className="button-text">{t('footer.cta_button', 'Jetzt Kerze anzünden')}</span>
            </button>
          </div>
          <div className="footer-divider"></div>
          <div className="footer-logo">{t('footer.logo', 'Place of Lights')}</div>
          <p className="footer-text">{t('footer.tagline', 'Ein Meer aus Licht, verbunden durch Menschlichkeit')}</p>
          <p className="footer-copyright">{t('footer.copyright', '© 2025 Place of Lights · Alle Rechte vorbehalten')}</p>
        </div>
      </footer>

      {/* Meditation/Contemplation Overlay */}
      {meditationCandle && (
        <div className="meditation-overlay" onClick={closeMeditation}>
          <div className="meditation-content" onClick={(e) => e.stopPropagation()}>
            <button className="meditation-close" onClick={closeMeditation} data-testid="meditation-close">
              ×
            </button>
            
            {/* Realistic Candle */}
            <div className={`meditation-candle ${meditationCandle.size || 'medium'}`}>
              <div className="med-candle-wax">
                <div className="med-wax-drip"></div>
                <div className="med-wax-drip delay-1"></div>
              </div>
              <div className="med-wick"></div>
              <div className="med-flame">
                <div className="med-flame-outer"></div>
                <div className="med-flame-middle"></div>
                <div className="med-flame-inner"></div>
                <div className="med-flame-core"></div>
              </div>
              <div className="med-glow"></div>
            </div>
            
            {/* Candle Info */}
            <div className="meditation-info">
              <h2 className="meditation-name">{meditationCandle.name}</h2>
              {meditationCandle.message && (
                <p className="meditation-message">"{meditationCandle.message}"</p>
              )}
            </div>
            
            <p className="meditation-hint">{t('meditation.hint', 'Nimm dir einen Moment der Stille...')}</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default HomePage;
